import React, { useCallback, useRef, useState } from 'react';
import StreamCameraTile from './StreamCameraTile';

/**
 * 4-slot VideoWall for VGPS devices.
 *
 * Channel ID convention (identical to example/src/app/components/VideoWall.js):
 *   channelId='1' → CameraTile maps HD→'101', SD→'102'  (camera 1)
 *   channelId='2' → CameraTile maps HD→'201', SD→'202'  (camera 2)
 *
 * Each VGPS device has exactly 2 cameras. The 3rd and 4th tile slots are
 * capture-image placeholders (no active stream). Drag-and-drop is only
 * allowed between the 2 live camera slots.
 */

function PlaceholderTile({ title }) {
  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: '#1a1e2a',
      border: '0.5px solid #2e3445',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        position: 'absolute', top: 8, left: 8,
        padding: '3px 8px', borderRadius: 999,
        backgroundColor: 'rgba(217,4,47,0.92)',
        color: '#fff', fontWeight: 700, fontSize: 11,
      }}>
        {title}
      </div>
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="rgba(255,255,255,0.18)" strokeWidth="1.5" strokeLinecap="round">
        <path d="M23 7l-7 5 7 5V7z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </svg>
    </div>
  );
}

function buildSlots(deviceSerial) {
  const s = deviceSerial || '';
  return [
    { id: `${s}_ch1`, channelId: '1', name: 'CH1', isMain: true },
    { id: `${s}_ch2`, channelId: '2', name: 'CH2', isMain: false },
    { id: `${s}_cap1`, channelId: null, name: 'Chụp ảnh', isMain: false },
    { id: `${s}_cap2`, channelId: null, name: 'Chụp ảnh', isMain: false },
  ];
}

function TileRenderer({ slot, deviceSerial }) {
  if (!slot.channelId) {
    return <PlaceholderTile title={slot.name} />;
  }
  return (
    <StreamCameraTile
      deviceSerial={deviceSerial}
      channelId={slot.channelId}
      title={slot.name}
      height="100%"
    />
  );
}

const VideoWall = React.memo(({ deviceSerial }) => {
  const [slots, setSlots] = useState(() => buildSlots(deviceSerial));
  const dragIdRef = useRef(null);

  React.useEffect(() => {
    setSlots(buildSlots(deviceSerial));
  }, [deviceSerial]);

  const handleDragStart = useCallback((id) => { dragIdRef.current = id; }, []);
  const handleDragOver = useCallback((e) => { e.preventDefault(); }, []);
  const handleDragEnd = useCallback(() => { dragIdRef.current = null; }, []);

  const handleDrop = useCallback((targetId) => {
    const fromId = dragIdRef.current;
    dragIdRef.current = null;
    if (!fromId || fromId === targetId) return;
    setSlots((prev) => {
      const next = prev.map((s) => ({ ...s }));
      const fi = next.findIndex((s) => s.id === fromId);
      const ti = next.findIndex((s) => s.id === targetId);
      if (fi === -1 || ti === -1) return prev;
      [next[fi], next[ti]] = [next[ti], next[fi]];
      const prevMain = prev.find((s) => s.isMain);
      if (prevMain && (fromId === prevMain.id || targetId === prevMain.id)) {
        const newMainId = fromId === prevMain.id ? targetId : fromId;
        next.forEach((s) => { s.isMain = s.id === newMainId; });
      }
      return next;
    });
  }, []);

  const mainSlot = slots.find((s) => s.isMain) ?? slots[0];
  const otherSlots = slots.filter((s) => s !== mainSlot);

  if (!deviceSerial || !mainSlot) {
    return (
      <div style={{ height: 'calc(100vh - 66px)', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111' }}>
        <span style={{ color: '#aaa', fontSize: 14 }}>Chọn thiết bị để xem camera</span>
      </div>
    );
  }

  return (
    <div style={{ height: 'calc(100vh - 66px)', display: 'flex', flexDirection: 'column', backgroundColor: '#0d1117' }}>
      {/* Main tile — fills remaining height */}
      <div
        style={{ flex: 1, minHeight: 0, overflow: 'hidden', cursor: 'move' }}
        draggable
        onDragStart={() => handleDragStart(mainSlot.id)}
        onDragOver={handleDragOver}
        onDrop={() => handleDrop(mainSlot.id)}
        onDragEnd={handleDragEnd}
      >
        <TileRenderer slot={mainSlot} deviceSerial={deviceSerial} />
      </div>

      {/* Bottom row — 3 equal tiles at 22vh */}
      <div style={{
        height: '22vh',
        minHeight: 60,
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 1,
        backgroundColor: '#222',
        overflow: 'hidden',
      }}>
        {otherSlots.map((slot) => (
          <div
            key={slot.id}
            style={{ overflow: 'hidden', cursor: slot.channelId ? 'move' : 'default', minWidth: 0 }}
            draggable={!!slot.channelId}
            onDragStart={() => slot.channelId && handleDragStart(slot.id)}
            onDragOver={handleDragOver}
            onDrop={() => handleDrop(slot.id)}
            onDragEnd={handleDragEnd}
          >
            <TileRenderer slot={slot} deviceSerial={deviceSerial} />
          </div>
        ))}
      </div>

      {/* Red toolbar */}
      <div style={{
        height: 44,
        flexShrink: 0,
        backgroundColor: '#D9042F',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        padding: '0 25px',
        boxSizing: 'border-box',
      }}>
        <div style={{ cursor: 'pointer' }}>
          <img src="/static/iconSvg/grid/grid4_icon.svg" alt="Grid layout" />
        </div>
      </div>
    </div>
  );
});

export default VideoWall;
